import { useState, useEffect, useRef } from 'react'
import { Table, Card, Button, Space, Modal, Form, Input, Select, DatePicker, Switch, InputNumber, message, Tag, Popconfirm, Image } from 'antd'
import { PlusOutlined, SearchOutlined, ReloadOutlined, EditOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons'
import axios from 'axios'
import dayjs from 'dayjs'
import { adsApi } from '../services/api'

const { Option } = Select
const { RangePicker } = DatePicker

interface Ad {
  id: number
  title: string
  image_url: string
  link_url: string
  position: number
  sort: number
  status: number
  start_time: string
  end_time: string
  created_at: string
}

const positionMap: Record<number, string> = {
  1: '首页轮播',
  2: '分类页横幅',
  3: '首页弹窗',
  4: '活动页横幅'
}

const platformMap: Record<number, string> = {
  1: '个人端',
  2: '企业端'
}

export default function Ads() {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<Ad[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [modalVisible, setModalVisible] = useState(false)
  const [currentAd, setCurrentAd] = useState<Ad | null>(null)
  const [form] = Form.useForm()
  const [searchForm] = Form.useForm()
  const [uploadLoading, setUploadLoading] = useState(false)
  const fileInputRef = useRef<any>(null)

  useEffect(() => {
    loadData()
  }, [page, pageSize])

  const loadData = async () => {
    setLoading(true)
    try {
      const values = searchForm.getFieldsValue()
      const res = await adsApi.getList({
        page,
        page_size: pageSize,
        platform: values.platform,
        position: values.position,
        status: values.status,
        keyword: values.keyword
      })
      if (res.code === 200) {
        setData(res.data?.list || res.data || [])
        setTotal(res.data?.total || res.data?.length || 0)
      }
    } catch (err) {
      message.error('加载广告列表失败')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = () => {
    setPage(1)
    loadData()
  }

  const handleReset = () => {
    searchForm.resetFields()
    setPage(1)
    loadData()
  }

  const handleCreate = () => {
    form.resetFields()
    form.setFieldsValue({ status: 1, sort: 0, position: 1 })
    setCurrentAd(null)
    setModalVisible(true)
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const isImg = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'].includes(file.type)
    if (!isImg) { message.error('只支持 JPG/PNG/WebP 格式'); return }
    if (file.size / 1024 / 1024 > 5) { message.error('图片不能超过 5MB'); return }
    setUploadLoading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const res: any = await axios.post('/api/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${localStorage.getItem('token') || ''}` }
      })
      if (res.data.code === 200) {
        form.setFieldsValue({ image_url: res.data.data.url })
        message.success('上传成功')
      } else {
        message.error(res.data.message || '上传失败')
      }
    } catch { message.error('上传失败') }
    finally { setUploadLoading(false); if (fileInputRef.current) fileInputRef.current.value = '' }
  }

  const handleEdit = (record: Ad) => {
    setCurrentAd(record)
    form.setFieldsValue({
      ...record,
      time_range: record.start_time && record.end_time ? [dayjs(record.start_time), dayjs(record.end_time)] : null
    })
    setModalVisible(true)
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      const submitData = {
        ...values,
        start_time: values.time_range?.[0]?.toISOString(),
        end_time: values.time_range?.[1]?.toISOString()
      }
      delete submitData.time_range

      let res
      if (currentAd) {
        res = await adsApi.update(currentAd.id, submitData)
      } else {
        res = await adsApi.create(submitData)
      }
      if (res.code === 200) {
        message.success(currentAd ? '更新成功' : '创建成功')
        setModalVisible(false)
        loadData()
      } else {
        message.error(res.message || '操作失败')
      }
    } catch (err) {
      message.error('提交失败')
    }
  }

  const handleDelete = async (id: number) => {
    try {
      const res = await adsApi.delete(id)
      if (res.code === 200) {
        message.success('删除成功')
        loadData()
      } else {
        message.error(res.message || '删除失败')
      }
    } catch (err) {
      message.error('删除失败')
    }
  }

  const handleToggleStatus = async (record: Ad) => {
    try {
      const res = await adsApi.update(record.id, { status: record.status === 1 ? 0 : 1 })
      if (res.code === 200) {
        message.success(record.status === 1 ? '已停用' : '已启用')
        loadData()
      } else {
        message.error(res.message || '操作失败')
      }
    } catch (err) {
      message.error('操作失败')
    }
  }

  const columns = [
    {
      title: '图片',
      dataIndex: 'image_url',
      key: 'image_url',
      width: 120,
      render: (v: string) => v ? <Image src={v} width={100} height={60} style={{ objectFit: 'cover' }} /> : '-'
    },
    { title: '标题', dataIndex: 'title', key: 'title', width: 150 },
    {
      title: '位置',
      dataIndex: 'position',
      key: 'position',
      width: 100,
      render: (v: number) => <Tag color="blue">{positionMap[v] || '未知'}</Tag>
    },
    {
      title: '平台',
      dataIndex: 'platform',
      key: 'platform',
      width: 80,
      render: (v: number) => <Tag color="purple">{platformMap[v] || '未知'}</Tag>
    },
    { title: '排序', dataIndex: 'sort', key: 'sort', width: 80 },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (v: number) => <Tag color={v === 1 ? 'green' : 'default'}>{v === 1 ? '启用' : '停用'}</Tag>
    },
    { title: '开始时间', dataIndex: 'start_time', key: 'start_time', width: 160, render: (v: string) => v?.substring(0, 16).replace('T', ' ') },
    { title: '结束时间', dataIndex: 'end_time', key: 'end_time', width: 160, render: (v: string) => v?.substring(0, 16).replace('T', ' ') },
    {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_: any, record: Ad) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
          <Button type="link" size="small" onClick={() => handleToggleStatus(record)}>
            {record.status === 1 ? '停用' : '启用'}
          </Button>
          <Popconfirm title="确认删除该广告?" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Form form={searchForm} layout="inline" style={{ marginBottom: 16 }}>
          <Form.Item name="keyword">
            <Input placeholder="标题" style={{ width: 150 }} />
          </Form.Item>
          <Form.Item name="platform">
            <Select placeholder="平台" style={{ width: 100 }} allowClear>
              {Object.entries(platformMap).map(([k, v]) => <Option key={k} value={Number(k)}>{v}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="position">
            <Select placeholder="位置" style={{ width: 120 }} allowClear>
              {Object.entries(positionMap).map(([k, v]) => <Option key={k} value={Number(k)}>{v}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="status">
            <Select placeholder="状态" style={{ width: 100 }} allowClear>
              <Option value={1}>启用</Option>
              <Option value={0}>停用</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>新增广告</Button>
            </Space>
          </Form.Item>
        </Form>

        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps) }
          }}
        />
      </Card>

      {/* 新增/编辑弹窗 */}
      <Modal
        title={currentAd ? '编辑广告' : '新增广告'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={600}
      >
        <Form form={form} labelCol={{ span: 5 }} wrapperCol={{ span: 18 }}>
          <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入标题' }]}>
            <Input placeholder="请输入广告标题" />
          </Form.Item>
          <Form.Item name="image_url" label="广告图片" rules={[{ required: true, message: '请上传图片' }]} extra="支持 JPG/PNG/WebP，建议 1200×280px">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              <Button
                icon={<UploadOutlined />}
                loading={uploadLoading}
                onClick={() => fileInputRef.current?.click()}
              >
                上传图片
              </Button>
              {form.getFieldValue('image_url') && (
                <Image
                  src={form.getFieldValue('image_url')}
                  width={120}
                  height={60}
                  style={{ objectFit: 'cover', borderRadius: 4 }}
                />
              )}
            </div>
          </Form.Item>
          <Form.Item name="link_url" label="跳转链接">
            <Input placeholder="点击后跳转的链接" />
          </Form.Item>
          <Form.Item name="position" label="位置" rules={[{ required: true, message: '请选择位置' }]}>
            <Select placeholder="请选择广告位置">
              {Object.entries(positionMap).map(([k, v]) => <Option key={k} value={Number(k)}>{v}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="platform" label="平台" rules={[{ required: true, message: '请选择平台' }]}>
            <Select placeholder="请选择平台">
              {Object.entries(platformMap).map(([k, v]) => <Option key={k} value={Number(k)}>{v}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="sort" label="排序" extra="数字越小越靠前">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="status" label="状态" valuePropName="checked" getValueFromEvent={(checked) => checked ? 1 : 0} getValueProps={(value) => ({ checked: value === 1 })}>
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>
          <Form.Item name="time_range" label="有效期">
            <RangePicker showTime style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
